import { type UseQueryResult } from '@tanstack/react-query';
import { z } from 'zod';

import { useApiQuery } from '../../../lib/api/hooks.js';
import { queryKeys } from '../../../lib/api/query-keys.js';
import { notificationTemplateEndpoints } from './endpoints.js';
import {
  notificationTemplateDetail,
  notificationTemplateRow,
  notificationTemplateTags,
  type NotificationTemplateDetail,
  type NotificationTemplateRow,
  type NotificationTemplateTag,
} from './schemas.js';

/**
 * Reads for the Notification Templates module.
 *
 * The service stores email and SMS rows in different tables but exposes them as
 * one list filtered by `channel`. The list page therefore fetches for the
 * channel the reader has chosen, and the row schema is shared.
 */

export function useNotificationTemplateDetail(
  templateId: string,
): UseQueryResult<NotificationTemplateDetail> {
  return useApiQuery({
    queryKey: queryKeys.notificationTemplates.detail(templateId),
    path: notificationTemplateEndpoints.detail(templateId),
    schema: notificationTemplateDetail,
  });
}

export function useNotificationTemplates(
  channel: string,
  propertyId?: string,
): UseQueryResult<readonly NotificationTemplateRow[]> {
  return useApiQuery({
    queryKey: queryKeys.notificationTemplates.list({
      scope: { channel, ...(propertyId === undefined ? {} : { propertyId }) },
    }),
    path: notificationTemplateEndpoints.list,
    query: { channel, ...(propertyId === undefined ? {} : { propertyId }) },
    schema: z.array(notificationTemplateRow),
  });
}

export function useNotificationTemplateTags(): UseQueryResult<readonly NotificationTemplateTag[]> {
  return useApiQuery({
    queryKey: queryKeys.notificationTemplateTags,
    path: notificationTemplateEndpoints.tags,
    schema: notificationTemplateTags,
  });
}
